import { MapPin, Copyright, Code, Mail, Phone, Clock, Facebook, Instagram, Youtube } from 'lucide-react';

export function Footer() {
	return (
		<footer className="w-full bg-primary dark:bg-surface-dark pt-12 pb-6 border-t-[6px] border-secondary transition-colors duration-300">
			<div className="w-11/12 lg:w-[85%] max-w-7xl mx-auto flex flex-col text-white font-roboto">

				{/* Top Section: Grid */}
				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12 mb-12 px-4">

					{/* Office Hours */}
					<div className="flex flex-col gap-6">
						<h3 className="text-secondary font-bold text-xs uppercase tracking-[0.2em]">
							HORÁRIO DE ATENDIMENTO
						</h3>

						<div className="space-y-6">
							<div className="flex items-start gap-4 group">
								<div className="p-2 bg-white/10 rounded-lg group-hover:bg-secondary group-hover:text-primary transition-all duration-300">
									<Clock size={18} />
								</div>
								<div className="flex flex-col">
									<span className="font-bold text-sm tracking-wide">Gerais nas Secretarias</span>
									<span className="text-white/60 text-xs">13h às 19h • Segunda a Sexta</span>
								</div>
							</div>

							<div className="flex items-start gap-4 group">
								<div className="p-2 bg-white/10 rounded-lg group-hover:bg-secondary group-hover:text-primary transition-all duration-300">
									<Clock size={18} />
								</div>
								<div className="flex flex-col">
									<span className="font-bold text-sm tracking-wide">Praça do Cidadão</span>
									<span className="text-white/60 text-xs">08h às 19h • Segunda a Sexta</span>
								</div>
							</div>
						</div>
					</div>

					{/* Contact Info */}
					<div className="flex flex-col gap-6">
						<h3 className="text-secondary font-bold text-xs uppercase tracking-[0.2em]">
							CONTATO OFICIAL
						</h3>

						<div className="space-y-4">
							<a href="tel:4733416000" className="flex items-center gap-4 text-white no-underline group">
								<div className="p-2 bg-white/10 rounded-lg group-hover:bg-secondary group-hover:text-primary transition-all duration-200">
									<Phone size={18} />
								</div>
								<span className="text-sm font-medium hover:text-secondary transition-colors">(47) 3341-6000</span>
							</a>

							<a href="mailto:prefeitura@itajai.sc.gov.br" className="flex items-center gap-4 text-white no-underline group">
								<div className="p-2 bg-white/10 rounded-lg group-hover:bg-secondary group-hover:text-primary transition-all duration-200">
									<Mail size={18} />
								</div>
								<span className="text-sm font-medium hover:text-secondary transition-colors">prefeitura@itajai.sc.gov.br</span>
							</a>

							<div className="mt-8 flex items-center gap-5 p-5 bg-gradient-to-br from-white/5 to-white/[0.02] rounded-[2rem] border border-white/10 shadow-2xl relative overflow-hidden group hover:border-secondary/30 transition-all duration-500">
								<div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:animate-shine" />
								<img
									src="/assets/imgs/Selo_de_Transparencia.webp"
									alt="Selo de Transparência Ouro 2024"
									className="w-16 h-16 object-contain filter drop-shadow-[0_0_8px_rgba(255,215,0,0.3)] transition-all duration-700 group-hover:scale-110 group-hover:rotate-6"
								/>
								<div className="flex flex-col">
									<h4 className="text-secondary font-black text-sm tracking-tight m-0 uppercase line-clamp-1">Selo Ouro 2024</h4>
									<p className="text-white/50 text-[10px] m-0 font-medium uppercase tracking-widest leading-none mt-1">Transparência Pública</p>
									<span className="text-[8px] text-white/30 font-bold uppercase tracking-tighter mt-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-500">Radar Nacional</span>
								</div>
							</div>
						</div>
					</div>

					{/* Social & Connect */}
					<div className="flex flex-col gap-6">
						<h3 className="text-secondary font-bold text-xs uppercase tracking-[0.2em]">
							CONECTE-SE CONOSCO
						</h3>
						<p className="text-white/60 text-sm leading-relaxed">
							Acompanhe as notícias e serviços da nossa cidade em tempo real.
						</p>
						<nav className="flex gap-4" aria-label="Redes sociais">
							{[
								{ icon: <Facebook />, url: "https://www.facebook.com/prefeituradeitajai", label: "Facebook" },
								{ icon: <Instagram />, url: "https://www.instagram.com/prefeituradeitajai/", label: "Instagram" },
								{ icon: <Youtube />, url: "https://www.youtube.com/user/prefeituratajai", label: "YouTube" }
							].map((social, idx) => (
								<a
									key={idx}
									href={social.url}
									target="_blank"
									rel="noreferrer"
									className="flex items-center justify-center w-12 h-12 bg-white/10 rounded-2xl text-white transition-all duration-300 hover:bg-secondary hover:text-primary hover:-translate-y-2 shadow-lg"
									aria-label={social.label}
								>
									{social.icon}
								</a>
							))}
						</nav>
					</div>
				</div>

				{/* Location Bar */}
				<div className="w-full py-8 border-y border-white/10 flex flex-col lg:flex-row items-center justify-center gap-6 text-center lg:text-left mb-8">
					<div className="text-secondary bg-white/10 p-3 rounded-full shadow-inner">
						<MapPin size={22} />
					</div>
					<div className="flex flex-col items-center lg:items-start gap-1">
						<div className="flex flex-wrap items-center justify-center gap-2 text-sm text-white/80">
							<span className="font-bold uppercase tracking-wide text-white">MUNICIPIO DE ITAJAI</span>
							<span className="hidden lg:inline text-white/20">•</span>
							<span className="font-medium whitespace-nowrap">CNPJ: 83.102.277/0001-52</span>
						</div>
						<div className="flex flex-wrap items-center justify-center gap-2 text-xs text-white/60">
							<span>Rua Alberto Werner, 100</span>
							<span className="hidden lg:inline text-white/20">•</span>
							<span>Vila Operária</span>
							<span className="hidden lg:inline text-white/20">•</span>
							<span>Itajaí - SC</span>
							<span className="hidden lg:inline text-white/20">•</span>
							<span>CEP: 88304-053</span>
						</div>
					</div>
				</div>

				{/* Bottom Bar: Copyright & Credits */}
				<div className="flex flex-col md:flex-row items-center justify-between gap-6 px-4">
					<div className="flex items-center gap-3 text-[0.625rem] uppercase font-bold tracking-widest text-white/40">
						<Copyright size={14} />
						<span>{new Date().getFullYear()} Município de Itajaí • Todos os direitos reservados</span>
					</div>

					<div className="flex items-center gap-3 py-2 px-4 bg-white/5 rounded-full border border-white/10">
						<Code size={14} className="text-secondary" />
						<span className="text-[0.625rem] font-bold uppercase tracking-wider text-white/70">Desenvolvimento: <span className="text-white">SETEC</span></span>
					</div>
				</div>
			</div>
		</footer>
	);
}